import { useEffect, useState } from "react";
import { translationApi } from "@services/translationApi";

interface JobCost {
  /** Total cost across all records for the job, in USD. Null while unknown. */
  totalCostUsd: number | null;
  totalTokens: number;
  loading: boolean;
  /**
   * True once a cost lookup has resolved (success, empty, or error). Lets the
   * UI distinguish "still loading" from "resolved with no cost record" — the
   * latter happens for cache-hit translations, which reuse a cached result and
   * so never record a new cost under their job id.
   */
  settled: boolean;
}

/**
 * Fetch the total cost (USD) for a translation or batch job.
 *
 * A job may have one cost record per target language; this sums them into a
 * single per-job total. Cost is only recorded once a job completes, so pass
 * `enabled=false` (or a null id) while the job is still running to avoid
 * needless requests. Failures are swallowed and surfaced as a null cost — cost
 * display is informational and must never disrupt the main view.
 */
export const useJobCost = (jobId: string | null, enabled = true): JobCost => {
  const [totalCostUsd, setTotalCostUsd] = useState<number | null>(null);
  const [totalTokens, setTotalTokens] = useState(0);
  const [loading, setLoading] = useState(false);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!jobId || !enabled) {
      setTotalCostUsd(null);
      setTotalTokens(0);
      setSettled(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setSettled(false);

    void (async () => {
      try {
        const res = await translationApi.getCostByJobId(jobId);
        if (cancelled) return;
        if (res.records.length === 0) {
          setTotalCostUsd(null);
          setTotalTokens(0);
          return;
        }
        const cost = res.records.reduce((sum, r) => sum + (r.total_cost_usd ?? 0), 0);
        const tokens = res.records.reduce((sum, r) => sum + (r.total_tokens ?? 0), 0);
        setTotalCostUsd(cost);
        setTotalTokens(tokens);
      } catch {
        if (!cancelled) {
          setTotalCostUsd(null);
          setTotalTokens(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSettled(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [jobId, enabled]);

  return { totalCostUsd, totalTokens, loading, settled };
};

/**
 * Format a USD cost for display. Uses more decimal places for small amounts so
 * sub-cent translation costs remain visible (e.g. "$0.0012").
 */
export const formatUsd = (amount: number): string => {
  const decimals = amount > 0 && amount < 0.01 ? 4 : amount < 1 ? 3 : 2;
  return `$${amount.toFixed(decimals)}`;
};
